import { Button } from '@web-app/components/ui/Button/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@web-app/components/ui/Dialog/dialog';
import { Textarea } from '@web-app/components/ui/Textarea/textarea';
import {
  EARLY_CALL_END_REASONS,
  type EarlyCallEndReason,
  earlyCallEndReasonSchema,
  END_REASON_OTHER_MAX_LENGTH,
} from '@web-app/utils/appointment/early-call-end-reason';
import { useEffect, useId, useRef, useState } from 'react';

interface GracePeriodModalProps {
  gracePeriodEndsAt: number;
  onClose: () => void;
  onKeepWaiting: () => void;
  onEndCall: () => void;
  onSaveEndReason: (reason: EarlyCallEndReason) => Promise<void>;
  onExpired: () => void;
}

export const GracePeriodModal = ({
  gracePeriodEndsAt,
  onClose,
  onKeepWaiting,
  onEndCall,
  onSaveEndReason,
  onExpired,
}: GracePeriodModalProps) => {
  const keepWaitingRef = useRef<HTMLButtonElement>(null);
  const hasNotifiedExpiry = useRef(false);
  const isActive = useRef(true);
  const isSubmitting = useRef(false);
  const reasonId = useId();
  const otherId = useId();
  const [isChoosingReason, setIsChoosingReason] = useState(false);
  const [endReason, setEndReason] = useState('');
  const [endReasonOther, setEndReasonOther] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const parsedReason = earlyCallEndReasonSchema.safeParse({
    endReason,
    endReasonOther,
  });
  const [now, setNow] = useState(Date.now);
  const secondsRemaining = Math.max(
    0,
    Math.ceil((gracePeriodEndsAt - now) / 1000),
  );
  const minutes = Math.floor(secondsRemaining / 60);
  const seconds = secondsRemaining % 60;
  const hasExpired = secondsRemaining === 0;

  useEffect(() => {
    isActive.current = true;
    return () => {
      isActive.current = false;
    };
  }, []);

  const confirmEarlyEnd = async () => {
    if (!parsedReason.success || isSubmitting.current) return;
    // Check the actual clock in case the tab resumed before the next timer tick.
    if (Date.now() >= gracePeriodEndsAt) {
      setNow(Date.now());
      return;
    }

    isSubmitting.current = true;
    setIsSaving(true);
    setSaveError(null);
    try {
      await onSaveEndReason(parsedReason.data);
      if (isActive.current && !hasNotifiedExpiry.current) onEndCall();
    } catch {
      if (isActive.current)
        setSaveError('Could not save your reason. Please try again.');
    } finally {
      isSubmitting.current = false;
      if (isActive.current) setIsSaving(false);
    }
  };

  useEffect(() => {
    if (hasExpired) return;

    // Re-read the clock so a backgrounded tab cannot extend the grace period.
    const interval = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(interval);
  }, [hasExpired]);

  useEffect(() => {
    if (!hasExpired || hasNotifiedExpiry.current) return;

    hasNotifiedExpiry.current = true;
    onExpired();
  }, [hasExpired, onExpired]);

  if (hasExpired) return null;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        initialFocus={keepWaitingRef}
        showCloseButton={false}
        className="max-w-[min(448px,calc(100vw-2rem))] gap-0 p-0"
      >
        <DialogHeader>
          <DialogTitle>
            The 15-minute grace period hasn&apos;t ended yet
          </DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4 text-sm">
          <DialogDescription className="text-foreground">
            <strong>
              If you end this call now, you won’t be paid for this visit.
            </strong>{' '}
            Providers must wait the full 15 minutes before ending a no-show
            call.
          </DialogDescription>
          <p role="timer" className="font-semibold tabular-nums">
            {String(minutes).padStart(2, '0')}:
            {String(seconds).padStart(2, '0')} remaining
          </p>
          <p className="text-muted-foreground">
            About 1 in 10 no-shows are patients who join a few minutes late.
          </p>
          {isChoosingReason && (
            <div className="space-y-3">
              <div className="space-y-2">
                <label htmlFor={reasonId} className="block font-medium">
                  Reason for ending early (required)
                </label>
                <select
                  id={reasonId}
                  required
                  autoFocus
                  disabled={isSaving}
                  value={endReason}
                  onChange={(event) => {
                    setEndReason(event.target.value);
                    setSaveError(null);
                  }}
                  className="w-full rounded-md border border-input-legacy bg-card px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
                >
                  <option value="" disabled>
                    Select a reason
                  </option>
                  {Object.entries(EARLY_CALL_END_REASONS).map(
                    ([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ),
                  )}
                </select>
              </div>
              {endReason === 'other' && (
                <div className="space-y-2">
                  <label htmlFor={otherId} className="block font-medium">
                    Please describe your reason (required)
                  </label>
                  <Textarea
                    id={otherId}
                    required
                    disabled={isSaving}
                    maxLength={END_REASON_OTHER_MAX_LENGTH}
                    value={endReasonOther}
                    onChange={(event) => {
                      setEndReasonOther(event.target.value);
                      setSaveError(null);
                    }}
                  />
                </div>
              )}
              {saveError && (
                <p role="alert" className="text-destructive-legacy">
                  {saveError}
                </p>
              )}
            </div>
          )}
        </DialogBody>
        <DialogFooter className="flex-wrap gap-3">
          {isChoosingReason ? (
            <Button
              variant="outline"
              disabled={!parsedReason.success || isSaving}
              aria-busy={isSaving}
              onClick={confirmEarlyEnd}
            >
              End call without payment
            </Button>
          ) : (
            <Button variant="outline" onClick={() => setIsChoosingReason(true)}>
              End call anyway
            </Button>
          )}
          <Button
            render={<button type="button" ref={keepWaitingRef} />}
            onClick={onKeepWaiting}
          >
            Keep waiting
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
